import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { errorMessage } from '@/lib/errors';

const registerSchema = z.object({
  email: z.string().email({ message: 'Email inválido' }),
  password: z.string().min(8, { message: 'Mínimo 8 caracteres' }),
  confirmPassword: z.string(),
  full_name: z.string().min(2, { message: 'Nombre requerido' }),
  phone: z.string().optional(),
  user_type: z.enum(['client', 'provider'], { message: 'Selecciona un tipo de usuario' }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Las contraseñas no coinciden",
  path: ['confirmPassword'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

export const RegisterForm = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const passwordValue = watch('password', '');
  const passwordChecks = {
    length: (passwordValue?.length ?? 0) >= 8,
    uppercase: /[A-Z]/.test(passwordValue ?? ''),
    number: /[0-9]/.test(passwordValue ?? ''),
  };

  const onSubmit = async (data: RegisterFormData) => {
    setLoading(true);
    try {
      const { error, data: authData } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: data.full_name,
            phone: data.phone || null,
            user_type: data.user_type,
          },
        },
      });

      if (error) {
        if (errorMessage(error).includes('User already registered')) {
          toast({
            title: t('register.errorExistingTitle'),
            description: t('register.errorExisting'),
            variant: 'destructive',
          });
        } else {
          toast({
            title: t('register.errorGeneric'),
            description: errorMessage(error),
            variant: 'destructive',
          });
        }
      } else {
        toast({
          title: t('register.successTitle'),
          description: t('register.successDesc'),
        });

        // Si es proveedor, redirigir a onboarding
        if (data.user_type === 'provider') {
          navigate('/onboarding');
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      toast({
        title: t('register.errorGeneric'),
        description: t('register.errorGeneric'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        toast({
          title: t('register.errorGeneric'),
          description: errorMessage(error),
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: t('register.errorGeneric'),
        description: t('register.errorGoogle'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="w-full max-w-md space-y-6 p-8 rounded-2xl bg-[#1a1a1a]/80 backdrop-blur-sm border border-[#B8860B]/20 shadow-2xl">
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-bold text-white">{t('register.title')}</h1>
        <p className="text-gray-400">
          {t('register.subtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="full_name" className="text-gray-300">{t('register.fullNameLabel')}</Label>
          <Input
            id="full_name"
            type="text"
            placeholder={t('register.fullNamePlaceholder')}
            {...register('full_name')}
            disabled={loading}
            className="bg-[#0a0a0a] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#B8860B] focus:ring-[#B8860B]/20"
          />
          {errors.full_name && (
            <p className="text-sm text-red-400">{errors.full_name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-gray-300">{t('register.emailLabel')}</Label>
          <Input
            id="email"
            type="email"
            placeholder="tu@email.com"
            {...register('email')}
            disabled={loading}
            className="bg-[#0a0a0a] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#B8860B] focus:ring-[#B8860B]/20"
          />
          {errors.email && (
            <p className="text-sm text-red-400">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-gray-300">{t('register.phoneLabel')}</Label>
          <Input
            id="phone"
            type="tel"
            placeholder="+34 600 000 000"
            {...register('phone')}
            disabled={loading}
            className="bg-[#0a0a0a] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#B8860B] focus:ring-[#B8860B]/20"
          />
          {errors.phone && (
            <p className="text-sm text-red-400">{errors.phone.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-gray-300">{t('register.passwordLabel')}</Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            {...register('password')}
            disabled={loading}
            className="bg-[#0a0a0a] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#B8860B] focus:ring-[#B8860B]/20"
          />
          {errors.password && (
            <p className="text-sm text-red-400">{errors.password.message}</p>
          )}
          {passwordValue && (
            <div className="space-y-2 pt-1">
              <div className="flex gap-1.5">
                <div className={`h-1.5 flex-1 rounded-full transition-colors ${passwordChecks.length ? 'bg-green-500' : 'bg-gray-600'}`} />
                <div className={`h-1.5 flex-1 rounded-full transition-colors ${passwordChecks.uppercase ? 'bg-green-500' : 'bg-gray-600'}`} />
                <div className={`h-1.5 flex-1 rounded-full transition-colors ${passwordChecks.number ? 'bg-green-500' : 'bg-gray-600'}`} />
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                <span className={passwordChecks.length ? 'text-green-400' : 'text-gray-500'}>{t('register.pwChars')}</span>
                <span className={passwordChecks.uppercase ? 'text-green-400' : 'text-gray-500'}>{t('register.pwUppercase')}</span>
                <span className={passwordChecks.number ? 'text-green-400' : 'text-gray-500'}>{t('register.pwNumber')}</span>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-gray-300">{t('register.confirmPasswordLabel')}</Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="••••••••"
            {...register('confirmPassword')}
            disabled={loading}
            className="bg-[#0a0a0a] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#B8860B] focus:ring-[#B8860B]/20"
          />
          {errors.confirmPassword && (
            <p className="text-sm text-red-400">{errors.confirmPassword.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-gray-300">{t('register.userTypeLabel')}</Label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-gray-300 hover:text-white transition-colors">
              <input
                type="radio"
                value="client"
                {...register('user_type')}
                disabled={loading}
                className="w-4 h-4 text-[#B8860B] bg-[#0a0a0a] border-gray-700 focus:ring-[#B8860B]/20 accent-[#B8860B]"
              />
              <span>{t('register.client')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-gray-300 hover:text-white transition-colors">
              <input
                type="radio"
                value="provider"
                {...register('user_type')}
                disabled={loading}
                className="w-4 h-4 text-[#B8860B] bg-[#0a0a0a] border-gray-700 focus:ring-[#B8860B]/20 accent-[#B8860B]"
              />
              <span>{t('register.provider')}</span>
            </label>
          </div>
          {errors.user_type && (
            <p className="text-sm text-red-400">{errors.user_type.message}</p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full bg-[#B8860B] hover:bg-[#D4AF37] text-black font-semibold transition-all duration-300"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('register.creating')}
            </>
          ) : (
            t('register.createAccount')
          )}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-gray-700" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-[#1a1a1a] px-2 text-gray-500">
              {t('register.orContinueWith')}
            </span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full bg-transparent border-gray-700 text-white hover:bg-white/5 hover:border-[#B8860B]/50"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          {t('register.signUpGoogle')}
        </Button>
      </form>

      <p className="text-center text-sm text-gray-400">
        {t('register.hasAccount')}{' '}
        <Link to="/login" className="text-[#B8860B] hover:text-[#D4AF37] font-medium hover:underline">
          {t('register.signIn')}
        </Link>
      </p>
    </div>
  );
};
